import mixpanel from 'mixpanel-browser';

import type { AnalyticsPayload } from './events';

let initialized = false;

/**
 * Initialises the Mixpanel browser SDK. Must be called once at app startup.
 * No-ops on duplicate calls so screens that re-mount are safe.
 *
 * @param token - Mixpanel project token.
 * @param options - Optional overrides for `mixpanel.init()`.
 */
export function initMixpanel(
	token: string,
	isDev: boolean,
	options?: Partial<Parameters<typeof mixpanel.init>[1]>,
): void {
	if (initialized || !token) return;

	mixpanel.init(token, {
		persistence: 'localStorage',
		autocapture: true,
		debug: isDev,
		api_host: 'https://api-eu.mixpanel.com',
		...options,
	});

	initialized = true;
}

/**
 * Tracks an event in Mixpanel. Silently no-ops if the SDK has not been
 * initialised (e.g. token is missing in local dev).
 */
export function track(eventName: string, properties?: AnalyticsPayload): void {
	if (!initialized) return;
	mixpanel.track(eventName, properties ?? {});
}

/**
 * Associates the current anonymous Mixpanel profile with a known user ID.
 * Call after login once the Auth0 `sub` claim is available.
 */
export function identify(distinctId: string): void {
	if (!initialized || !distinctId) return;
	mixpanel.identify(distinctId);
}

/**
 * Sets persistent properties on the identified user's Mixpanel profile.
 */
export function setUserProperties(properties: Record<string, unknown>): void {
	if (!initialized) return;

	mixpanel.people.set({
		$name: properties.name,
		$email: properties.email,
	});
	mixpanel.register({ $email: properties.email, $name: properties.name });
}

/**
 * Clears the current user's identity and generates a new anonymous
 * distinct_id. Call on logout.
 */
export function resetIdentity(): void {
	if (!initialized) return;
	mixpanel.reset();
}
